import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { drName } from "@/lib/utils";

function fmt(d) { return d.toISOString().slice(0, 10); }
function nextDays(n) {
  const arr = []; const now = new Date();
  for (let i = 0; i < n; i++) { const d = new Date(now); d.setDate(now.getDate() + i); arr.push(d); }
  return arr;
}

export default function EmbedBooking() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [selectedDate, setSelectedDate] = useState(fmt(new Date()));
  const [slots, setSlots] = useState([]);
  const [dur, setDur] = useState(30);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ patient_name: "", patient_email: "", patient_phone: "" });
  const [sent, setSent] = useState(false);

  useEffect(() => { api.get(`/doctors/${id}`).then(r => setDoc(r.data)); }, [id]);
  useEffect(() => {
    if (doc) api.get(`/doctors/${id}/slots`, { params: { date: selectedDate } })
              .then(r => { setSlots(r.data.slots || []); setDur(r.data.duration_min || 30); });
  }, [id, selectedDate, doc]);

  const submit = async () => {
    if (!form.patient_name || !form.patient_email || !form.patient_phone) return toast.error("Completa todos los campos");
    try {
      await api.post("/appointments", { doctor_id: id, date: selectedDate, time: selected, ...form, booking_for: "self" });
      setSent(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const days = useMemo(() => nextDays(10), []);

  if (!doc) return <div className="p-4 text-slate-500 text-sm">Cargando…</div>;

  return (
    <div className="min-h-screen bg-slate-50 p-3" style={{ ["--brand"]: doc.widget_color || "#2563eb" }}>
      <Card className="max-w-md mx-auto border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-3">
            {doc.widget_logo ? (
              <img src={doc.widget_logo} className="w-12 h-12 rounded-xl object-cover bg-white p-1 border border-slate-200" alt=""/>
            ) : doc.photo_url && (
              <img src={doc.photo_url} className="w-12 h-12 rounded-xl object-cover" alt=""/>
            )}
            <div>
              <div className="font-semibold text-slate-900 text-sm">{drName(doc.full_name)}</div>
              <div className="text-xs font-semibold" style={{ color: doc.widget_color || "#2563eb" }}>{doc.specialty} · {doc.city}</div>
            </div>
          </div>
          {sent ? (
            <div className="text-center py-6">
              <div className="text-emerald-600 text-3xl">✓</div>
              <p className="text-sm text-slate-700 mt-2">Revisa tu email para confirmar tu cita (2h).</p>
            </div>
          ) : selected ? (
            <div className="space-y-2">
              <div className="text-xs text-slate-500">Reservando {selectedDate} · {selected} ({dur} min)</div>
              <Input data-testid="embed-name" placeholder="Nombre completo" value={form.patient_name} onChange={e => setForm({...form, patient_name: e.target.value})}/>
              <Input data-testid="embed-email" placeholder="Email" type="email" value={form.patient_email} onChange={e => setForm({...form, patient_email: e.target.value})}/>
              <Input data-testid="embed-phone" placeholder="Teléfono" value={form.patient_phone} onChange={e => setForm({...form, patient_phone: e.target.value})}/>
              <div className="flex gap-2">
                <Button data-testid="embed-back" size="sm" variant="outline" onClick={() => setSelected(null)} className="flex-1">Atrás</Button>
                <Button data-testid="embed-confirm" size="sm" onClick={submit} className="flex-1 text-white"
                        style={{ backgroundColor: doc.widget_color || "#2563eb" }}>Reservar</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex gap-1.5 overflow-x-auto pb-2">
                {days.map(d => {
                  const iso = fmt(d); const active = iso === selectedDate;
                  return (
                    <button key={iso} data-testid={`embed-date-${iso}`} onClick={() => setSelectedDate(iso)}
                            className="flex-shrink-0 px-2 py-1.5 rounded-lg text-xs min-w-[50px]"
                            style={{ backgroundColor: active ? (doc.widget_color || "#2563eb") : "#f1f5f9",
                                     color: active ? "#fff" : "#334155" }}>
                      <div>{d.getDate()}</div>
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-3 gap-1.5 mt-3">
                {slots.length === 0 ? <p className="col-span-3 text-xs text-slate-500 text-center py-4">Sin horarios</p> :
                  slots.map(s => (
                    <button key={s.time} data-testid={`embed-slot-${s.time}`} disabled={!s.available}
                            onClick={() => setSelected(s.time)}
                            className="px-2 py-2 rounded-md border border-slate-200 text-xs font-medium hover:bg-blue-50 disabled:opacity-30 disabled:line-through">
                      {s.time}
                    </button>
                  ))}
              </div>
              <div className="text-[10px] text-slate-400 text-center mt-3">Powered by Agendik</div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
